import { useMemo } from 'react';
import { Box, DataTable, DataTableBody, DataTableFooter, Flex, Spinner } from '@optiaxiom/react';
import {
  getCoreRowModel,
  PaginationState,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';

import { EmptyPage } from '@frontend/components';
import { SpecializedAgentExecution } from '@frontend/types/specialized-agent';

import createColumns from './columns';
import { executionsTableWrapper } from './SpecializedAgentExecutionsTable.css';

type SpecializedAgentExecutionsTableProps = {
  executions: SpecializedAgentExecution[];
  pagination?: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
  isLoading: boolean;
  error: Error | null;
  onExecutionSelect: (execution: SpecializedAgentExecution) => void;
  selectedExecutionId?: string | null;
  onPageSizeChange: (pageSize: number) => void;
  onPageChange: (page: number) => void;
  baselineEvaluationScore?: number;
};

export function SpecializedAgentExecutionsTable({
  executions,
  pagination,
  isLoading,
  error,
  onExecutionSelect,
  selectedExecutionId,
  onPageSizeChange,
  onPageChange,
  baselineEvaluationScore,
}: SpecializedAgentExecutionsTableProps) {
  const paginationState = useMemo(
    () => ({
      pageIndex: pagination ? pagination.page - 1 : 0,
      pageSize: pagination?.page_size ?? 20,
    }),
    [pagination]
  );

  const rowSelection: RowSelectionState = useMemo(
    () => (selectedExecutionId ? { [selectedExecutionId]: true } : {}),
    [selectedExecutionId]
  );

  const columns = useMemo(
    () => createColumns({ baselineEvaluationScore }),
    [baselineEvaluationScore]
  );

  const table = useReactTable({
    columns,
    data: executions,
    getCoreRowModel: getCoreRowModel(),
    // getFilteredRowModel: getFilteredRowModel(),
    getRowId: row => row.id,
    enableRowSelection: true,
    manualPagination: true,
    rowCount: pagination?.total_count,
    pageCount: pagination?.total_pages,
    state: {
      pagination: paginationState,
      columnPinning: { right: ['actions'] },
      rowSelection,
    },
    meta: {
      onRowClick: onExecutionSelect,
      selectedExecutionId,
    },
    onPaginationChange: (
      updater: PaginationState | ((old: PaginationState) => PaginationState)
    ) => {
      const newPagination = typeof updater === 'function' ? updater(paginationState) : updater;

      if (newPagination.pageIndex !== paginationState.pageIndex) {
        onPageChange(newPagination.pageIndex + 1);
      }
      if (newPagination.pageSize !== paginationState.pageSize && onPageSizeChange) {
        onPageSizeChange(newPagination.pageSize);
      }
    },
  });

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" h="224">
        <Spinner size="lg">Loading executions...</Spinner>
      </Flex>
    );
  }

  if (error) {
    return <EmptyPage title="Failed to load executions" description={error.message} />;
  }

  if (executions.length === 0) {
    return (
      <EmptyPage
        title="No executions found"
        description="Agent executions will appear here as they are performed."
      />
    );
  }

  return (
    <Box className={executionsTableWrapper}>
      <DataTable table={table}>
        <DataTableBody loading={isLoading} />
        <DataTableFooter showPageSizeOptions />
      </DataTable>
    </Box>
  );
}
